import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StarIcon from '@mui/icons-material/Star';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import WeekendIcon from '@mui/icons-material/Weekend';
import HomeIcon from '@mui/icons-material/Home';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import PersonIcon from '@mui/icons-material/Person';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { number: 1, label: '家具付き物件とは？その特徴と魅力', sectionId: 'about-furnished', icon: <HomeIcon fontSize="small" sx={{ color: '#1e88e5' }} /> },
    { number: 2, label: '家具付き物件のメリット', sectionId: 'merits', icon: <CheckCircleIcon fontSize="small" sx={{ color: '#43a047' }} /> },
    { number: 3, label: '家具付き物件のデメリット', sectionId: 'demerits', icon: <WarningAmberIcon fontSize="small" sx={{ color: '#e65100' }} /> },
    { number: 4, label: '物件選びのコツ＆チェックリスト', sectionId: 'choosing-tips', icon: <TipsAndUpdatesIcon fontSize="small" sx={{ color: '#ffb300' }} /> },
    { number: 5, label: '体験談とリアルな声', sectionId: 'experiences', icon: <PersonIcon fontSize="small" sx={{ color: '#6d4c41' }} /> },
    { number: 6, label: 'まとめ', sectionId: 'conclusion', icon: <FormatListNumberedIcon fontSize="small" sx={{ color: '#1976d2' }} /> },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map(item => (
                                <ListItem key={item.sectionId} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#444', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.15rem' }}>
                                        {item.number}.
                                    </Box>
                                    <ListItemIcon sx={{ minWidth: 28 }}>{item.icon}</ListItemIcon>
                                    <Link component="button" onClick={() => scrollToSection(item.sectionId)} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left', lineHeight: 1.3 }}>
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    <FormatListNumberedIcon sx={{ mr: 0.5, fontSize: 18 }} /> 目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアで人気の「家具付き物件」の真価とは？
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアを始めるとき、多くの人が悩むのが「家具を揃えるべきか」「家具付き物件を選ぶべきか」というポイント。初期費用や引っ越しの手間を抑えたい方や、短期間だけ住みたい方にとって、家具付き物件は非常に魅力的な選択肢です。しかし一方で、「実際に住んでみてどうだった？」という声も多数。この記事では、家具付きルームシェア物件の特徴から、メリット・デメリット、物件選びのコツ、リアルな体験談まで、徹底的に解説します。
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    家具付き物件を選ぶ前に知っておきたいポイントを、専門家視点で分かりやすくご紹介します。
                </Typography>
            </Box>

            {/* セクション1: 家具付き物件とは？ */}
            <Box id="about-furnished" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家具付き物件とは？その特徴と魅力
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            家具付き物件とは、ベッドや机、椅子、冷蔵庫、洗濯機など、日常生活に必要な家具・家電があらかじめ備え付けられている賃貸物件のこと。特にルームシェア物件では、リビングやキッチンの共有家具・家電はもちろん、個室にもデスクや収納が用意されているケースが多く、入居してすぐに快適な生活がスタートできます。
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <WeekendIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="生活必需品が揃っている（ベッド・机・家電など）" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <MeetingRoomIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="個室・共用スペースともに家具完備が多い" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <StarIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="短期〜中長期滞在に向いている" />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1, width: '100%' }}>
                            <img
                                src="/images/blog/furnished-living_roomshare-furnished-merit-demerit.jpg"
                                alt="明るい共有リビングでのルームメイト同士の食事シーン。白いソファやウッドテーブル、観葉植物が置かれ、ナチュラルで温かな雰囲気の中、男女4人が談笑しながら朝食をとっている様子"
                                style={{ width: '100%', display: 'block' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    家具付き物件は、引っ越しや初期投資の負担を大幅に軽減できるのが最大の特徴です。
                </Typography>
            </Box>

            {/* セクション2: メリット */}
            <Box id="merits" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家具付き物件のメリット
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#f1f8e9', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
                        <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        こんなにある！家具付きの魅力
                    </Typography>
                    <List>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon sx={{ color: '#43a047' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="初期費用が圧倒的に安い"
                                secondary="家具・家電を新品で揃えると数十万円かかりますが、家具付きなら入居時の出費を大幅にカットできます。"
                            />
                        </ListItem>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon sx={{ color: '#43a047' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="引っ越しがとってもラク"
                                secondary="大型家具の搬入・搬出作業や手配が不要。身軽に入居・退去でき、短期利用にも最適です。"
                            />
                        </ListItem>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon sx={{ color: '#43a047' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="すぐに生活がスタートできる"
                                secondary="キッチン家電や調理器具も揃っていることが多く、着いたその日から自炊や快適な生活が可能です。"
                            />
                        </ListItem>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon sx={{ color: '#43a047' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="短期滞在やお試し移住にも最適"
                                secondary="転勤・留学・ワーキングホリデーなど、一定期間だけ住みたい方にピッタリです。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                        <Chip color="primary" variant="outlined" label="コスト削減" sx={{ mr: 1, mb: 1 }} />
                        <Chip color="primary" variant="outlined" label="即入居可能" sx={{ mr: 1, mb: 1 }} />
                        <Chip color="primary" variant="outlined" label="手間いらず" sx={{ mr: 1, mb: 1 }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                            <img
                                src="/images/blog/move-in-easy_roomshare-furnished-merit-demerit.jpg"
                                alt="スーツケース1つで新しい部屋に到着した若い女性が、備え付けのベッドに腰掛けてほっと一息ついている明るい室内。ナチュラル色の家具と白いカーテンで統一された快適な空間"
                                style={{ width: '100%', display: 'block' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    「とにかく身軽に新生活を始めたい」「余分な出費を抑えたい」方には、家具付き物件が断然おすすめです。
                </Typography>
            </Box>

            {/* セクション3: デメリット */}
            <Box id="demerits" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家具付き物件のデメリット
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'warning.main' }}>
                        <WarningAmberIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        注意したいポイント
                    </Typography>
                    <List>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon sx={{ color: '#e65100' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="家具のデザインや品質が選べない"
                                secondary="既存の家具が自分のセンスや快適さに合わない場合も。カスタマイズ不可な点に注意。"
                            />
                        </ListItem>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon sx={{ color: '#e65100' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="家賃がやや割高になることも"
                                secondary="家具・家電のコストが家賃に上乗せされるため、同じ立地・広さの家具なし物件より高くなる傾向があります。"
                            />
                        </ListItem>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon sx={{ color: '#e65100' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="家具の使用感や状態が気になる"
                                secondary="前の入居者が使った家具なので、使用感や消耗度合いは事前にチェックが必要です。"
                            />
                        </ListItem>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                            <ListItemIcon>
                                <ArrowForwardIosIcon sx={{ color: '#e65100' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="退去時のトラブルリスク"
                                secondary="家具を破損・汚損した場合の弁償や原状回復義務、追加費用について契約内容をしっかり確認しましょう。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    物件ごとに家具の状態や契約条件は異なります。事前の内見やオーナーへの確認が重要です。
                </Typography>
            </Box>

            {/* セクション4: 物件選びのコツ */}
            <Box id="choosing-tips" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    物件選びのコツ＆チェックリスト
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家具付き物件のルームシェアを選ぶ際、「こんなはずじゃなかった！」を防ぐために、以下のポイントをチェックしましょう。
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={1} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                                <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                                    <TipsAndUpdatesIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    チェックポイント
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="家具・家電のリストと状態を事前に確認" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="自分で持ち込みたい家具がある場合の可否" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="家具の破損時や退去時の対応ルール" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="家賃や共益費に家具代がどこまで含まれているか" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon>
                                            <CheckCircleIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="清掃・メンテナンスの頻度や担当者" />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                                <img
                                    src="/images/blog/furniture-check_roomshare-furnished-merit-demerit.jpg"
                                    alt="ルームシェア物件の内見で、備え付け家具の状態を1つずつ丁寧にチェックする男女2人。明るい昼間、木目調のデスクやクローゼットを確認している様子"
                                    style={{ width: '100%', display: 'block' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    見学時は、家具の傷や汚れ・不具合がないかも細かく見ておくと安心です。
                </Typography>
            </Box>

            {/* セクション5: 体験談 */}
            <Box id="experiences" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    体験談とリアルな声
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#0277bd' }} />
                                    20代女性・会社員
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>「引っ越しの荷物がスーツケース1つで済み、仕事が忙しい中とても助かりました。家具・家電が揃っていたので、即日生活が始められて感動！ただ、おしゃれな空間に自分なりのアレンジができない点は少し物足りなかったです。」</strong>
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#43a047' }} />
                                    30代男性・ITエンジニア
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>「家具付き物件は初期コストが抑えられるので、地方から東京への転職に最適。ベッドや冷蔵庫も新品同様で清潔感がありました。退去時、椅子の小キズで追加費用が発生しそうになったので、入居前の写真記録は必須だと思いました。」</strong>
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#fbc02d' }} />
                                    40代女性・フリーランス
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>「海外からの一時帰国で利用。生活必需品がすべて揃っていたので、短期間でも快適でした。ただ、前の住人の使い方によって家具の傷み具合に差があるので、内見は念入りに！」</strong>
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', boxShadow: 1, maxWidth: 500, mx: 'auto' }}>
                    <img
                        src="/images/blog/roommate-experience_roomshare-furnished-merit-demerit.jpg"
                        alt="明るい共有キッチンで、備え付けの調理家電を使って一緒に夕食を作る2人のルームメイト。木目調の家具と白い壁、清潔な空間で和やかな雰囲気"
                        style={{ width: '100%', display: 'block' }}
                    />
                </Box>
            </Box>

            {/* セクション6: まとめ */}
            <Box id="conclusion" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家具付き物件は、ルームシェア初心者や短期滞在者、コストを抑えたい方にとって非常に便利な選択肢。一方で、家具の状態や契約条件、カスタマイズ性には注意が必要です。自分のライフスタイルや目的に合った物件を選ぶために、事前のチェックを怠らないことが成功のカギとなります。
                </Typography>
                <Typography variant="body2" color="primary">
                    「家具付き物件＝身軽で快適な新生活」。でも、本当に満足できるかどうかは、あなたの目でしっかり見極めましょう！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-furnished-merit-demerit',
    slug: 'roomshare-furnished-merit-demerit',
    title: 'ルームシェアの「家具付き物件」メリット・デメリット',
    excerpt: '家具付き物件の特徴、メリット・デメリット、物件選びのコツ、体験談までルームシェア専門家が詳しく解説します。',
    coverImage: '/images/blog/cover-roomshare-furnished_roomshare-furnished-merit-demerit.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。ナチュラルな木目家具と清潔感ある白い壁、観葉植物が配されたリラックスした雰囲気。',
    publishedAt: '2024-06-12T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '物件選び', '暮らしの知恵'],
    tags: ['家具付き物件', 'ルームシェア', 'メリット', 'デメリット', '体験談'],
    author: {
        name: '高橋 理恵',
        avatar: '/images/avatars/takahashi-rie.jpg',
        bio: 'ルームシェア・共同生活・住まい選びに詳しいコンサルタント。多くの実体験と調査をもとに、分かりやすくアドバイス。',
        profilePicture: '/images/avatars/takahashi-rie.jpg'
    },
    viewCount: 1780,
    contentComponent: PostContent
};

export default post;